// Blocking profile-completion gate (fotoğraf #1). Shown when the logged-in user
// is missing a required contact field (name / phone). Collect + save via
// PATCH /auth/me; on success the parent refetches /me and the gate clears. A
// KVKK Aydınlatma link sits at the bottom when a published notice exists.
//
// Phone is collected only (no SMS/OTP at V1) — no format validation beyond
// "not empty" so any international number is accepted as typed.

import { useState, type FormEvent, type ReactNode } from 'react'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { useMutation } from '@tanstack/react-query'
import { DialogOverlay, DialogTitle } from '../../components/ui/dialog'
import { DialogPortal } from '../../components/ui/dialog.lib'
import { Button } from '../../components/ui/button'
import { cn } from '../../lib/utils'
import { updateProfile } from '../../api/account'
import { useLocaleStore } from '../../store/locale'
import { uiToAgreementLocale } from '../../i18n/agreement-locales'
import { pickT } from '../../i18n'
import { profileCompletionI18n } from '../../i18n/onboarding/profile-completion'
import { TermsFullTextModal } from './TermsFullTextModal'
import type { MarketingConsentRef, PendingConsent } from '../../lib/auth'

const inputCls =
  'w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-800 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500'

type KvkkRef = PendingConsent | MarketingConsentRef

export function ProfileCompletionGate({
  initialName,
  initialPhone,
  kvkk,
  onDone,
}: {
  initialName: string
  initialPhone: string | null
  kvkk?: KvkkRef | null
  onDone: () => void
}) {
  const locale = useLocaleStore((s) => s.locale)
  const t = pickT(profileCompletionI18n, locale)
  const [name, setName] = useState(initialName ?? '')
  const [phone, setPhone] = useState(initialPhone ?? '')
  const [errors, setErrors] = useState<{ name?: string; phone?: string }>({})
  const [textOpen, setTextOpen] = useState(false)
  const [signLocale, setSignLocale] = useState(() => uiToAgreementLocale(locale))

  const mutation = useMutation({
    mutationFn: () => updateProfile({ display_name: name.trim(), phone: phone.trim() }),
    onSuccess: () => onDone(),
    onError: () =>
      setErrors((e) => ({
        ...e,
        phone: t.saveFailed,
      })),
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const next: { name?: string; phone?: string } = {}
    if (!name.trim()) next.name = t.nameRequired
    if (!phone.trim()) next.phone = t.phoneRequired
    setErrors(next)
    if (Object.keys(next).length) return
    mutation.mutate()
  }

  return (
    <DialogPrimitive.Root open>
      <DialogPortal>
        <DialogOverlay className="bg-slate-900/60" />
        <DialogPrimitive.Content
          onEscapeKeyDown={(e) => e.preventDefault()}
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
          className={cn(
            'fixed left-1/2 top-1/2 z-50 grid w-full max-w-[calc(100vw-1.5rem)] sm:max-w-md',
            '-translate-x-1/2 -translate-y-1/2 gap-4 border border-slate-200 bg-white p-5 sm:p-6',
            'shadow-xl rounded-lg max-h-[calc(100dvh-1.5rem)] sm:max-h-[92vh] overflow-y-auto',
          )}
        >
          <form className="flex flex-col gap-4" onSubmit={submit} noValidate>
            <div className="flex flex-col gap-1">
              <DialogTitle>
                {t.title}
              </DialogTitle>
              <p className="text-sm text-slate-600">{t.intro}</p>
            </div>

            <Field label={t.nameLabel} required error={errors.name}>
              <input
                className={inputCls}
                value={name}
                onChange={(e) => setName(e.target.value)}
                aria-label={t.nameLabel}
                data-testid="profile-name"
              />
            </Field>

            <Field label={t.phoneLabel} required error={errors.phone}>
              <input
                className={inputCls}
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                inputMode="tel"
                autoComplete="tel"
                placeholder="+90 5xx xxx xx xx"
                aria-label={t.phoneLabel}
                data-testid="profile-phone"
              />
            </Field>

            {kvkk && (
              <p className="text-xs text-slate-500">
                {t.kvkkBefore}
                <button
                  type="button"
                  className="font-medium text-brand-700 underline"
                  onClick={() => setTextOpen(true)}
                >
                  {kvkk.title}
                </button>
                {t.kvkkAfter}
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              disabled={mutation.isPending}
              data-testid="profile-submit"
            >
              {mutation.isPending ? t.saving : t.submit}
            </Button>
          </form>

          {kvkk && (
            <TermsFullTextModal
              agreementId={kvkk.agreement_id}
              title={kvkk.title}
              open={textOpen}
              onOpenChange={setTextOpen}
              locale={signLocale}
              onLocaleChange={setSignLocale}
            />
          )}
        </DialogPrimitive.Content>
      </DialogPortal>
    </DialogPrimitive.Root>
  )
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string
  required?: boolean
  error?: string
  children: ReactNode
}) {
  return (
    <label className="flex flex-col gap-1 text-sm">
      <span className="font-medium text-slate-700">
        {label} {required && <span className="text-rose-600">*</span>}
      </span>
      {children}
      {error && <span className="text-xs text-rose-600">{error}</span>}
    </label>
  )
}
